import {
    BreadcrumbList,
    Comment,
    CommentAction,
    DiscussionForumPosting,
    FAQPage,
    ListItem,
    Question,
    WithContext,
} from "schema-dts";

import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { counsellingsHomeUrl, counsellingsPostDetailUrl } from "@/mk2/apps/counsellings/urls";
import { topicUrl } from "@/mk2/apps/forum/helpers";
import {
    CategoryEntity,
    ForumMessageEntity,
    PostEntity,
    TopicEntity,
} from "@/mk2/apps/forum/schemas";
import { forumPostDetailUrl, forumTopicsCategoryUrl, forumUrl } from "@/mk2/apps/forum/urls";
import { largestAvailablePhotoVersionOfPost } from "@/mk2/apps/forum/utils";
import { AST } from "@/mk2/apps/wiki/schemas";
import { wikiArticleUrl } from "@/mk2/apps/wiki/utils";
import { getAllPlaintext } from "@/mk2/helpers/article_ast/utils";
import {
    getPhotoResizedDimensions,
    getUrlOfFirstImageOrFallback,
} from "@/mk2/helpers/structuredData";
import { YMDHMSIso } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { PhotoEntity, WikiArticleEntity } from "@/mk2/schemas";
import { ASK_AN_EXPERT, FORUM } from "@/mk/autogenerated/translations/structuredData.c02ede25e8f86ab9300b15ddcf7e1e9e";
import { photoUrl } from "@/mk/photo/photoUrl";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

// Schema.org BCP-47 language tag derived from the active server (201=SK, 202=CZ).
const SITE_LANGUAGE = ACTIVE_SERVER_ID === 202 ? "cs" : "sk";

const breadcrumbCounsellings = (baseUrl: string): ListItem => ({
    "@type": "ListItem",
    position: 1,
    item: {
        "@id": baseUrl + counsellingsHomeUrl,
        name: ASK_AN_EXPERT,
    },
});

const breadcrumbForum = (baseUrl: string): ListItem => ({
    "@type": "ListItem",
    position: 1,
    item: {
        "@id": baseUrl + forumUrl,
        name: FORUM,
    },
});

const breadcrumbSubforum = (baseUrl: string, subforum: string, subforumUrl: string): ListItem => ({
    "@type": "ListItem",
    position: 2,
    item: {
        "@id": baseUrl + subforumUrl,
        name: subforum,
    },
});

const breadcrumbTopic = (baseUrl: string, topic: TopicEntity): ListItem => ({
    "@type": "ListItem",
    position: 2,
    item: {
        "@id": baseUrl + topicUrl(topic),
        name: topic.name,
    },
});

const breadcrumbCategory = (baseUrl: string, category: CategoryEntity): ListItem => ({
    "@type": "ListItem",
    position: 2,
    item: {
        "@id": baseUrl + url(forumTopicsCategoryUrl, { categorySlug: category.slug }),
        name: category.name,
    },
});

const breadcrumbPost = (
    baseUrl: string,
    topic: TopicEntity,
    post: PostEntity,
    route = forumPostDetailUrl,
): ListItem => ({
    "@type": "ListItem",
    position: 3,
    item: {
        "@id": baseUrl + url(route, { topicSlug: topic.slug, postSlug: post.slug }),
        name: post.heading,
    },
});

export function breadcrumbsCounsellings(
    baseUrl,
    topic?: TopicEntity,
    post?: PostEntity,
): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbCounsellings(baseUrl)];

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post, counsellingsPostDetailUrl));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function breadcrumbsForum(
    baseUrl,
    category?: CategoryEntity,
    topic?: TopicEntity,
    post?: PostEntity,
): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbForum(baseUrl)];

    if (category) {
        items.push(breadcrumbCategory(baseUrl, category));
    }

    if (topic) {
        items.push(breadcrumbTopic(baseUrl, topic));
    }

    if (post) {
        items.push(breadcrumbPost(baseUrl, topic, post));
    }

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

export function breadcrumbsSubforum(
    baseUrl: string,
    subForum: string,
    subforumUrl: string,
): WithContext<BreadcrumbList> {
    const items: ListItem[] = [breadcrumbForum(baseUrl)];

    items.push(breadcrumbSubforum(baseUrl, subForum, subforumUrl));

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
    };
}

function postPageUrl(baseUrl: string, post: PostEntity): string {
    return baseUrl + url(forumPostDetailUrl, { topicSlug: post.topic.slug, postSlug: post.slug });
}

// Bare @id references to the review articles linked to a discussion (the
// "Súvisiace recenzie produktov" panel). We deliberately emit ONLY the @id — no
// @type/name/url — so Google does not classify these as standalone Product nodes
// and demand offers/review/aggregateRating (which would fail, since a discussion
// page is not the product's primary page). The @id matches the "#Product" node
// emitted by reviewArticleWiki() on the product page, where the full Product
// (ratings, offers, brand) is defined and validated.
function linkedReviewArticleRefs(
    baseUrl: string,
    linkedReviewArticles?: WikiArticleEntity[],
): Array<{ "@id": string }> {
    return (linkedReviewArticles || []).map((article) => ({
        "@id": baseUrl + wikiArticleUrl(article) + "#Product",
    }));
}

export function discussionForumPosting(
    baseUrl: string,
    post: PostEntity,
    comments?: ForumMessageEntity[],
    linkedReviewArticles?: WikiArticleEntity[],
): WithContext<DiscussionForumPosting> {
    const createdOn: string = YMDHMSIso(post.created);
    // dateModified = most recent of last reply / AI summary refresh.
    // sort().pop() works because ISO 8601 strings sort lexicographically by time.
    const lastActivity = [post.lastMessageTime, post.aiSummaryUpdatedAt]
        .filter(Boolean)
        .sort()
        .pop();
    const modifiedOn: string = YMDHMSIso(lastActivity);
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);
    const perexAstPlaintext = post.perexAST && getAllPlaintext(post.perexAST).trim();
    const aiSummaryPlaintext = post.aiSummaryAST && getAllPlaintext(post.aiSummaryAST).trim();
    const pageUrl = postPageUrl(baseUrl, post);
    const productRefs = linkedReviewArticleRefs(baseUrl, linkedReviewArticles);

    const commentItems: Comment[] = (comments || [])
        .filter((c) => !c.deletedAt)
        .reduce<Comment[]>((acc, c) => {
            const text = c.textAST ? getAllPlaintext(c.textAST).trim() : "";
            if (!text) return acc;
            acc.push({
                "@type": "Comment",
                text,
                datePublished: YMDHMSIso(c.created),
                url: pageUrl + "#comment-" + c.id,
                ...(c.authorUser?.username && {
                    author: {
                        "@type": "Person",
                        name: c.authorUser.username,
                        url: baseUrl + url(blogsProfileUrl, { username: c.authorUser.username }),
                    },
                }),
            });
            return acc;
        }, []);

    // fields according to https://developers.google.com/search/docs/appearance/structured-data/discussion-forum
    return {
        "@context": "https://schema.org",
        "@type": "DiscussionForumPosting",
        "@id": pageUrl + "#post",
        headline: post.heading,
        text: perexAstPlaintext ? perexAstPlaintext : post.heading,
        image: mainPhoto ? photoUrl(mainPhoto, mainPhotoVersion) : undefined,
        url: pageUrl,
        inLanguage: SITE_LANGUAGE,
        datePublished: createdOn,
        dateModified: createdOn !== modifiedOn ? modifiedOn : undefined,
        author: post.author?.username && {
            "@type": "Person",
            name: post.author.username,
            url: baseUrl + url(blogsProfileUrl, { username: post.author.username }),
        },
        interactionStatistic: {
            "@type": "InteractionCounter",
            interactionType: { "@type": "CommentAction" },
            userInteractionCount: post.comments,
        },
        ...(commentItems.length > 0 && { comment: commentItems }),
        ...(aiSummaryPlaintext && { abstract: aiSummaryPlaintext }),
        // Linked review products surfaced in the "Súvisiace recenzie produktov"
        // panel — referenced (not "about") since a thread merely relates to them.
        ...(productRefs.length > 0 && { mentions: productRefs }),
    };
}

interface FaqPair {
    question: string;
    answer: string;
}

// Walk the AST and produce a structured plaintext that preserves heading and
// bold markers — enough to regex out the FAQ section's Q/A pairs reliably.
function astToStructuredText(node: AST | null | undefined): string {
    if (!node) return "";
    if (node.type === "plain_text") return node.text || "";
    if (node.type === "br") return "\n";
    if (node.type === "h2" || node.type === "h3" || node.type === "h4") {
        const inner = (node.elements || []).map(astToStructuredText).join("");
        return `\n## ${inner.trim()}\n`;
    }
    if (node.type === "bold") {
        const inner = (node.elements || []).map(astToStructuredText).join("");
        return `**${inner}**`;
    }
    if (node.type === "paragraph") {
        return (node.elements || []).map(astToStructuredText).join("") + "\n";
    }
    if (node.elements) return node.elements.map(astToStructuredText).join("");
    return "";
}

function extractFaqsFromAst(ast: AST | null | undefined): FaqPair[] {
    if (!ast) return [];
    const text = astToStructuredText(ast);
    // Match legacy "## FAQ", Slovak "## Najčastejšie otázky", and Czech
    // "## Nejčastější otázky" / "## Často kladené otázky" section headers.
    const slice = text.match(
        /\n##\s*(?:FAQ|Najčastejšie\s+otázky|Nejčastější\s+otázky|Často\s+kladené\s+otázky)\s*\n([\s\S]*?)(?=\n##\s|$)/i,
    );
    if (!slice) return [];
    const body = slice[1];
    const pairRe =
        /\*\*Q:\*\*\s*([\s\S]*?)\s*\n\s*\*\*A:\*\*\s*([\s\S]*?)(?=\n\s*\*\*Q:\*\*|\s*$)/g;
    const pairs: FaqPair[] = [];
    let m: RegExpExecArray | null;
    while ((m = pairRe.exec(body)) !== null) {
        const q = m[1].replace(/\s+/g, " ").trim();
        const a = m[2].replace(/\s+/g, " ").trim();
        if (q && a) pairs.push({ question: q, answer: a });
    }
    return pairs;
}

export function discussionFAQPage(baseUrl: string, post: PostEntity): WithContext<FAQPage> | null {
    if (!post.aiSummaryAST) return null;
    const faqs = extractFaqsFromAst(post.aiSummaryAST);
    if (faqs.length === 0) return null;
    const pageUrl = postPageUrl(baseUrl, post);
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": pageUrl + "#faq",
        about: { "@id": pageUrl + "#post" },
        inLanguage: SITE_LANGUAGE,
        // Transparent AI authorship — important signal for AI search engines
        // (ChatGPT, Perplexity, Bing Chat) and for Google's E-E-A-T evaluation.
        creator: {
            "@type": "Organization",
            name: "Modrý koník AI Summary",
            description: "Automated discussion summarization service",
        },
        dateCreated: post.aiSummaryUpdatedAt || undefined,
        dateModified: post.aiSummaryUpdatedAt || undefined,
        mainEntity: faqs.map<Question>((f) => ({
            "@type": "Question",
            name: f.question,
            inLanguage: SITE_LANGUAGE,
            acceptedAnswer: {
                "@type": "Answer",
                text: f.answer,
                inLanguage: SITE_LANGUAGE,
            },
        })),
    };
}

function mainPhotoOfPost(post: PostEntity): { photo: PhotoEntity; version: string } {
    if (post.coverPhoto) {
        return {
            photo: post.coverPhoto,
            version: largestAvailablePhotoVersionOfPost(post.coverPhoto, "s1600x1600"),
        };
    } else if (post.messagePhotos?.length > 0) {
        return {
            photo: post.messagePhotos[0].photofile,
            version: largestAvailablePhotoVersionOfPost(
                post.messagePhotos[0].photofile,
                "s1600x1600",
            ),
        };
    } else if (post.photos?.length > 0) {
        return {
            photo: post.photos[0],
            version: largestAvailablePhotoVersionOfPost(post.photos[0], "s1600x1600"),
        };
    } else if (post.topic?.coverPhoto) {
        return {
            photo: post.topic.coverPhoto,
            version: "s1600x1600",
        };
    } else {
        return {
            photo: null,
            version: null,
        };
    }
}

export function topicOG(baseUrl: string, topic: TopicEntity) {
    const dimensions = topic.coverPhoto ? getPhotoResizedDimensions(topic.coverPhoto, 1600) : null;

    return {
        "og:image": getUrlOfFirstImageOrFallback(topic.coverPhoto),
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
    };
}

export function postOG(baseUrl: string, post: PostEntity) {
    const { photo: mainPhoto, version: mainPhotoVersion } = mainPhotoOfPost(post);
    const imageUrl = mainPhoto
        ? photoUrl(mainPhoto, mainPhotoVersion)
        : getUrlOfFirstImageOrFallback(mainPhoto);

    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    return {
        "og:image": imageUrl,
        ...(dimensions && {
            "og:image:width": String(dimensions.width),
            "og:image:height": String(dimensions.height),
        }),
    };
}
